/**
 * "New client report page" — mint a campaign report page for one GAM order.
 * Moved as-is from the original tab, plus the "Show on the … advertiser page"
 * checkbox (sends `advertiserPageSlug`) when a hub covers the order, and the
 * reporting-window-end requirement for unlimited-flight orders.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal } from '../../ui';
import {
  BrandPreview,
  ReportableOrder,
  ReportWindowMode,
  createCampaignReportPage,
  getBrandPreview,
} from '../../../services/campaignReportsApi';
import type { ApiError } from '../../../services/api';
import {
  EXPIRY_DAYS_AFTER_FLIGHT_END,
  Notify,
  addDaysYmd,
  defaultCampaignSlug,
  fieldStyle,
  fmtDate,
  fmtInt,
  helpStyle,
  isYmd,
  labelStyle,
  localSlugify,
  pinProblem,
  randomPin,
  summaryStripStyle,
} from './shared';

interface CampaignCreateModalProps {
  order: ReportableOrder;
  /** Advertiser hub covering this order, when one exists — offers listing the page on it. */
  hub: { slug: string; label: string } | null;
  /** Pre-check the hub checkbox (default true whenever a hub exists). */
  linkToHubByDefault?: boolean;
  onClose: () => void;
  onCreated: (minted: { url: string; pin: string; label: string }) => void;
  notify: Notify;
}

const CampaignCreateModal: React.FC<CampaignCreateModalProps> = ({
  order,
  hub,
  linkToHubByDefault = true,
  onClose,
  onCreated,
  notify,
}) => {
  const [creating, setCreating] = useState(false);
  const [clientLabel, setClientLabel] = useState(order.advertiserName);
  const [slugInput, setSlugInput] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [pin, setPin] = useState<string>(() => randomPin());
  const [reportStart, setReportStart] = useState(order.startDate || '');
  const [requestedEnd, setRequestedEnd] = useState(order.endDate || '');
  const [windowMode, setWindowMode] = useState<ReportWindowMode>(order.endDate ? 'follow_campaign' : 'fixed');
  const [linkToHub, setLinkToHub] = useState(!!hub && linkToHubByDefault);
  // Brand suggestion (backend derives the advertised brand from GAM order +
  // line-item names, verbatim-validated — advertisers are often parent
  // companies). Applied to the label only while the admin hasn't edited it.
  const [brandPreview, setBrandPreview] = useState<BrandPreview | null>(null);
  const [brandLoading, setBrandLoading] = useState(true);
  const labelEditedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setBrandLoading(true);
    getBrandPreview(order.gamOrderId)
      .then((preview) => {
        if (cancelled) return;
        setBrandPreview(preview);
        setBrandLoading(false);
        if (!labelEditedRef.current && preview.suggestedLabel) setClientLabel(preview.suggestedLabel);
      })
      .catch(() => {
        if (!cancelled) setBrandLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [order.gamOrderId]);

  const effectiveSlug = useMemo(() => {
    if (slugTouched) return localSlugify(slugInput);
    return defaultCampaignSlug(order.gamOrderName, reportStart || order.startDate);
  }, [slugTouched, slugInput, order.gamOrderName, order.startDate, reportStart]);

  // Unlimited-flight orders take their flight end from the requested end date.
  const flightEnd = order.endDate || (isYmd(requestedEnd) ? requestedEnd : '');
  const expiryPreview = flightEnd ? addDaysYmd(flightEnd, EXPIRY_DAYS_AFTER_FLIGHT_END) : null;
  const noEndDate = !order.endDate;

  const mint = async () => {
    if (!clientLabel.trim()) return notify('error', 'Client label is required');
    const pinIssue = pinProblem(pin);
    if (pinIssue) return notify('error', pinIssue);
    if (reportStart && !isYmd(reportStart)) return notify('error', 'Reporting start must be a date');
    if (requestedEnd && !isYmd(requestedEnd)) return notify('error', 'Reporting end must be a date');
    if (reportStart && requestedEnd && requestedEnd < reportStart) {
      return notify('error', 'Reporting end must not precede its start');
    }
    if (noEndDate && !requestedEnd) {
      return notify('error', 'Reporting window end is required', 'This GAM order has no end date (unlimited flight).');
    }
    setCreating(true);
    try {
      const { page, url } = await createCampaignReportPage({
        gamOrderId: order.gamOrderId,
        slug: effectiveSlug || undefined,
        pin,
        clientLabel: clientLabel.trim(),
        reportStartDate: reportStart || undefined,
        requestedEndDate: requestedEnd || undefined,
        reportingWindowMode: windowMode,
        advertiserPageSlug: hub && linkToHub ? hub.slug : undefined,
      });
      onCreated({ url, pin: page.pin, label: page.clientLabel });
    } catch (e: unknown) {
      const err = e as ApiError;
      const title =
        err?.status === 409
          ? 'Could not create page'
          : err?.code === 'order_not_approved'
            ? 'GAM order not approved yet'
            : err?.code === 'no_end_date'
              ? 'Reporting window end is required'
              : 'Failed to create page';
      notify('error', title, err?.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Campaign reports"
      title="New client report page"
      width={640}
      closeOnEsc={false}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={mint} disabled={creating}>
            {creating ? 'Creating…' : 'Create page & generate report'}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={summaryStripStyle}>
          <div style={{ fontWeight: 700 }}>{order.gamOrderName}</div>
          <div style={{ color: 'var(--fg-3)', fontSize: 12.5, marginTop: 2 }}>
            {order.advertiserName} · Order {order.gamOrderId} · Flight {fmtDate(order.startDate)} →{' '}
            {order.endDate ? fmtDate(order.endDate) : 'no end date'}
            {typeof order.totalImpressionsDelivered === 'number' && (
              <> · {fmtInt(order.totalImpressionsDelivered)} impressions delivered</>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Client label *</label>
            <input
              style={fieldStyle}
              value={clientLabel}
              onChange={(e) => {
                labelEditedRef.current = true;
                setClientLabel(e.target.value);
              }}
              placeholder="Ben & Jerry’s"
            />
            <div style={helpStyle}>
              Shown as the report title: “{clientLabel || '…'} — Campaign Performance Report”.{' '}
              {brandLoading ? (
                <>Detecting the advertised brand from GAM metadata…</>
              ) : brandPreview && brandPreview.brands.length > 0 ? (
                <>Brand detected in the campaign’s GAM metadata: <strong>{brandPreview.brands.join(' & ')}</strong> — edit if wrong.</>
              ) : brandPreview ? (
                <>No distinct brand found in GAM metadata — defaulting to the advertiser ({brandPreview.advertiserName}).</>
              ) : null}
            </div>
          </div>
          <div>
            <label style={labelStyle}>Passcode (4 digits) *</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                style={{ ...fieldStyle, letterSpacing: 4, fontFamily: 'monospace', width: 90 }}
                value={pin}
                maxLength={4}
                inputMode="numeric"
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              />
              <Button size="sm" variant="ghost" onClick={() => setPin(randomPin())}>🎲 New</Button>
            </div>
          </div>
        </div>

        <div>
          <label style={labelStyle}>Reporting window</label>
          <select style={fieldStyle} value={windowMode} onChange={(e) => {
            const mode = e.target.value as ReportWindowMode;
            setWindowMode(mode);
            if (mode === 'follow_campaign') {
              setReportStart(order.startDate);
              setRequestedEnd(order.endDate);
            }
          }}>
            {!noEndDate && <option value="follow_campaign">Follow campaign dates in GAM</option>}
            <option value="fixed">Fixed reporting dates</option>
          </select>
          <div style={helpStyle}>Following GAM includes campaign extensions automatically. Fixed dates stay unchanged.</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Reporting window start</label>
            <input type="date" style={fieldStyle} value={reportStart} onChange={(e) => { setReportStart(e.target.value); setWindowMode('fixed'); }} />
          </div>
          <div>
            <label style={labelStyle}>Reporting window end{noEndDate ? ' *' : ''}</label>
            <input type="date" style={fieldStyle} value={requestedEnd} onChange={(e) => { setRequestedEnd(e.target.value); setWindowMode('fixed'); }} />
          </div>
        </div>
        <div style={{ ...helpStyle, marginTop: -8 }}>
          {noEndDate ? (
            <>
              <strong style={{ color: 'var(--warning)' }}>This order has no end date in GAM (unlimited flight)</strong> — set the
              reporting window end explicitly. The page expires {EXPIRY_DAYS_AFTER_FLIGHT_END} days after that date.
            </>
          ) : (
            <>
              Impressions, clicks, CTR, Delivery Trends and device data refresh hourly. Reach refreshes daily.
              Editing either date selects a fixed reporting window.
            </>
          )}
        </div>

        <div>
          <label style={labelStyle}>Page URL</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, color: 'var(--fg-4)', whiteSpace: 'nowrap' }}>culturegenesis.com/campaigns/</span>
            <input
              style={{ ...fieldStyle, width: 260 }}
              value={slugTouched ? slugInput : effectiveSlug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlugInput(e.target.value);
              }}
              placeholder="ben-jerrys-bars-activation-jul-2026"
            />
            <span style={{ fontSize: 12.5, color: 'var(--fg-4)' }}>/report</span>
          </div>
          <div style={helpStyle}>
            Distinct per campaign — brands can have several report pages. The slug can’t be changed after creation.
          </div>
        </div>

        {hub && (
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 13 }}>
            <input type="checkbox" checked={linkToHub} onChange={(e) => setLinkToHub(e.target.checked)} style={{ marginTop: 3 }} />
            <span>
              Show on the <strong>{hub.label}</strong> advertiser page
              <div style={{ ...helpStyle, marginTop: 2 }}>
                Listed on /advertisers/{hub.slug}/reports; the advertiser passcode also opens this report.
              </div>
            </span>
          </label>
        )}

        <div style={{ fontSize: 12.5, color: 'var(--fg-3)' }}>
          {expiryPreview ? (
            <>
              Link expires <strong>{fmtDate(expiryPreview)}</strong> ({EXPIRY_DAYS_AFTER_FLIGHT_END} days after the flight
              ends — extendable later).
            </>
          ) : (
            <>Link expires {EXPIRY_DAYS_AFTER_FLIGHT_END} days after the flight ends.</>
          )}{' '}
          Report data generates from GAM right after creation (takes a few minutes).
        </div>
      </div>
    </Modal>
  );
};

export default CampaignCreateModal;
